// Field values coming from CMS content and product data don't consistently match casing (e.g. "True" vs "true").
export const fieldValuesEqual = (a, b) => {
  if (a == null || b == null) return a === b;
  return String(a).toLowerCase() === String(b).toLowerCase();
};

export const ACCESSORIES_PARAM_KEY = 'accessories';
export const PRODUCT_PARAM_KEY = 'product';
export const STEP_PARAM_KEY = 'step';
export const SUMMARY_STEP = 'summary';

const FIELD_SECTION_PREFIX = 'field:';
const ACCESSORY_SECTION_PREFIX = 'accessory:';

export const fieldSectionKey = (fieldId) =>
  fieldId ? `${FIELD_SECTION_PREFIX}${fieldId}` : null;
export const accessorySectionKey = (index) =>
  `${ACCESSORY_SECTION_PREFIX}${index}`;

// The "step" param is the 1-based position of the open step: the option steps come first, followed by the
// accessory groups (so with 4 options, the first accessory group is step 5). "summary" is the final step.
// Being position-based, it tells how far the visitor got without knowing which fields the page has.
export const stepFromSectionKey = (sectionKey, options) => {
  if (!sectionKey) return null;
  if (sectionKey.startsWith(FIELD_SECTION_PREFIX)) {
    const index = options.findIndex(
      (option) => fieldSectionKey(option.fieldId) === sectionKey
    );
    return index === -1 ? null : String(index + 1);
  }
  if (sectionKey.startsWith(ACCESSORY_SECTION_PREFIX)) {
    const index = Number(sectionKey.slice(ACCESSORY_SECTION_PREFIX.length));
    return String(options.length + index + 1);
  }
  return null;
};

// Visitors can't skip ahead: a step is only honoured when every option before it is answered,
// otherwise the first unanswered option is opened. Unknown steps behave like a missing one.
export const sectionKeyFromStep = (step, options, firstUnansweredFieldId) => {
  const fallback = fieldSectionKey(firstUnansweredFieldId);
  if (!step || !/^\d+$/.test(step)) return fallback;

  const stepNumber = Number(step);
  if (stepNumber < 1) return fallback;

  if (stepNumber <= options.length) {
    const firstUnansweredIndex =
      firstUnansweredFieldId == null
        ? options.length
        : options.findIndex(
            (option) => option.fieldId === firstUnansweredFieldId
          );
    return stepNumber - 1 <= firstUnansweredIndex
      ? fieldSectionKey(options[stepNumber - 1].fieldId)
      : fallback;
  }

  return firstUnansweredFieldId == null
    ? accessorySectionKey(stepNumber - options.length - 1)
    : fallback;
};

// "id:qty" pairs so a chosen quantity survives a page reload, e.g. "1234:2,2345:1".
export const serializeAccessoriesParam = (selectedAccessories) =>
  selectedAccessories
    .map(({ accessory, quantity }) => `${accessory.id}:${quantity}`)
    .join(',');

export const parseAccessoriesParam = (raw) => {
  if (!raw) return { entries: [], hasError: false };

  let hasError = false;
  // Keyed by id so a repeated id keeps its last quantity instead of duplicating the accessory.
  const quantitiesById = new Map();
  raw.split(',').forEach((part) => {
    const [id, qty] = part.split(':');
    const quantity = qty != null ? Number(qty) : 1;
    if (!id || !Number.isInteger(quantity) || quantity <= 0) {
      hasError = true;
      return;
    }
    quantitiesById.set(id, quantity);
  });

  return {
    entries: Array.from(quantitiesById, ([id, quantity]) => ({ id, quantity })),
    hasError,
  };
};

// One query param per field (e.g. braked=true), the resolved product's article number, a single "accessories" list param
// and the current "step".
// The product id is needed because a field combination can match more than one product (e.g. an unanswered/skippable
// field) - without pinning the exact product, a reload could resolve a different one with different accessories.
export const buildConfiguratorSearchParams = (
  selectedFilters,
  selectedAccessories,
  productId,
  step
) => {
  const params = new URLSearchParams();

  Object.entries(selectedFilters).forEach(([fieldId, fieldValue]) => {
    if (fieldValue != null) params.set(fieldId, fieldValue);
  });

  if (productId != null) params.set(PRODUCT_PARAM_KEY, productId);

  const accessoriesValue = serializeAccessoriesParam(selectedAccessories);
  if (accessoriesValue) params.set(ACCESSORIES_PARAM_KEY, accessoriesValue);

  if (step) params.set(STEP_PARAM_KEY, step);

  return params;
};

// Matches each option's fieldId against a query param, validating the value against the option's known values.
// Unknown params are ignored since campaign links add tracking params (utm_*, gclid, ...).
export const applyFieldParams = (params, options) => {
  const nextFilters = {};
  let hasError = false;

  options.forEach((option) => {
    const rawValue = params.get(option.fieldId);
    if (rawValue == null) return;

    const matchedValue = option.values.find((value) =>
      fieldValuesEqual(value.fieldValue, rawValue)
    )?.fieldValue;
    if (matchedValue == null) {
      hasError = true;
      return;
    }
    nextFilters[option.fieldId] = matchedValue;
  });

  return { nextFilters, hasError };
};

export const filterProductsByFilters = (products, filters) =>
  products.filter((product) =>
    Object.entries(filters).every(([fieldId, fieldValue]) =>
      (product.fields || []).some(
        (field) =>
          field.fieldId === fieldId &&
          fieldValuesEqual(field.fieldValue, fieldValue)
      )
    )
  );

// Ids from the URL are always strings while product ids may not be.
const findProductById = (products, productId) =>
  products.find((product) => String(product.id) === String(productId));

// A field combination (e.g. one field left unanswered) can match more than one product, so the exact
// product must be pinned by id. Falls back to the first match, flagging a mismatch when the pin is stale/invalid.
export const resolveProductIdFromPin = (matches, requestedProductId) => {
  if (matches.length === 0) {
    return { productId: null, mismatch: !!requestedProductId };
  }
  if (requestedProductId) {
    const pinned = findProductById(matches, requestedProductId);
    if (pinned) return { productId: pinned.id, mismatch: false };
    return { productId: matches[0].id, mismatch: true };
  }
  return { productId: matches[0].id, mismatch: false };
};

// Keeps the currently pinned product while it still matches the filters; otherwise the first match.
// With no filters every product vacuously matches, so nothing is picked.
export const pickProductId = (products, filters, preferredProductId) => {
  if (Object.keys(filters).length === 0) return null;
  const matches = filterProductsByFilters(products, filters);
  const preferred =
    preferredProductId != null && findProductById(matches, preferredProductId);
  return (preferred || matches[0])?.id ?? null;
};

// No value of this option matches any product given the other filters - the step has nothing to
// select and is skipped, so it shouldn't block the configuration from being considered complete.
export const isOptionSkippable = (option, filters, products) =>
  option.values.every(
    (value) =>
      filterProductsByFilters(products, {
        ...filters,
        [option.fieldId]: value.fieldValue,
      }).length === 0
  );

// Everything the configurator needs to restore itself from a URL, in one place.
export const readConfiguratorUrlState = (params, options, products) => {
  const { nextFilters: filters, hasError: hasFieldError } = applyFieldParams(
    params,
    options
  );
  const matches =
    Object.keys(filters).length > 0
      ? filterProductsByFilters(products, filters)
      : [];
  const requestedProductId = params.get(PRODUCT_PARAM_KEY);
  const { productId, mismatch } = resolveProductIdFromPin(
    matches,
    requestedProductId
  );

  const firstUnansweredFieldId =
    options.find(
      (option) =>
        filters[option.fieldId] == null &&
        !isOptionSkippable(option, filters, products)
    )?.fieldId ?? null;
  const step = params.get(STEP_PARAM_KEY);

  return {
    filters,
    productId,
    requestedProductId,
    matchedProductIds: matches.map((product) => product.id),
    mismatch,
    hasError: hasFieldError || mismatch,
    rawAccessories: params.get(ACCESSORIES_PARAM_KEY),
    firstUnansweredFieldId,
    isSummaryStep: step === SUMMARY_STEP,
    openSectionKey: sectionKeyFromStep(step, options, firstUnansweredFieldId),
  };
};
